import React, { useState } from 'react';
import { naoPerturbeAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plug, Download, Link2, Copy, Check, Loader2, Unplug, Chrome, CheckCircle2, AlertTriangle, Clock, ExternalLink, Radio } from 'lucide-react';

const fmtDataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-');
export const PORTAL_LNP_URL = 'https://app.conexaoclarobrasil.com.br/controle/telefonia/consulta-lei-nao-perturbe';
const abrirPortal = () => window.open(PORTAL_LNP_URL, '_blank', 'noopener');

const StatusConector = ({ c }) => {
  if (!c?.pareado) return <Badge variant="outline" className="gap-1 text-gray-500" data-testid="conector-status"><Plug className="w-3 h-3" /> Não instalado</Badge>;
  if (!c.ativo) return <Badge className="gap-1 bg-amber-500 hover:bg-amber-500" data-testid="conector-status"><Clock className="w-3 h-3" /> Navegador offline</Badge>;
  if (c.sessao === 'caiu') return <Badge className="gap-1 bg-red-600 hover:bg-red-600" data-testid="conector-status"><AlertTriangle className="w-3 h-3" /> Sessão do portal caiu</Badge>;
  if (!c.token_atualizado_em && !c.ultima_consulta_em) return <Badge className="gap-1 bg-amber-500 hover:bg-amber-500" data-testid="conector-status"><Clock className="w-3 h-3" /> Aguardando login no portal</Badge>;
  return <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600" data-testid="conector-status"><CheckCircle2 className="w-3 h-3" /> Ativo</Badge>;
};

const Passo = ({ n, children }) => (
  <li className="flex gap-2.5">
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-600 text-[11px] font-bold text-white">{n}</span>
    <span className="text-gray-600 dark:text-gray-300">{children}</span>
  </li>
);

export const NaoPerturbeConector = ({ conector, onAtualizar }) => {
  const [link, setLink] = useState('');
  const [gerando, setGerando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [baixando, setBaixando] = useState(false);

  const gerar = async () => {
    setGerando(true);
    try {
      const res = await naoPerturbeAPI.conectorParear();
      setLink(res.data.link);
      toast.success('Link gerado — válido por 15 minutos');
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setGerando(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast.error('Não foi possível copiar — selecione o texto manualmente');
    }
  };

  const baixar = async () => {
    setBaixando(true);
    try {
      const res = await naoPerturbeAPI.conectorDownload();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'conector-crm-multitelecom.zip'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setBaixando(false);
    }
  };

  const remover = async () => {
    if (!window.confirm('Desconectar o conector? O navegador pareado deixará de consultar para o CRM.')) return;
    try {
      await naoPerturbeAPI.conectorRemover();
      toast.success('Conector desconectado');
      setLink('');
      onAtualizar?.();
    } catch (e) {
      toast.error(formatarErroAPI(e));
    }
  };

  return (
    <Card data-testid="conector-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Chrome className="w-4 h-4" /> Conector no navegador do admin</CardTitle>
            <CardDescription>Faz as consultas pela sessão e pelo IP do seu navegador — imune ao bloqueio do servidor e sem copiar token. Basta estar logado no portal.</CardDescription>
          </div>
          <StatusConector c={conector} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {conector?.pareado && (
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${conector.ativo && conector.sessao === 'ok' ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20' : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'}`}
            data-testid="conector-portal-box"
          >
            <div className="flex items-center gap-2 text-sm">
              <Radio className={`w-4 h-4 ${conector.ativo && conector.sessao === 'ok' ? 'text-emerald-600 animate-pulse' : 'text-amber-600'}`} />
              {conector.ativo && conector.sessao === 'ok'
                ? <span className="text-emerald-800 dark:text-emerald-200"><b>Consultando automaticamente</b> pela sessão de {conector.usuario_token || 'usuário do portal'} — cada venda nova é verificada em segundos.</span>
                : !conector.ativo
                  ? <span className="text-amber-800 dark:text-amber-200"><b>Navegador do conector fechado.</b> Abra o Chrome/Edge onde o conector está instalado.</span>
                  : <span className="text-amber-800 dark:text-amber-200"><b>Faça login no portal</b> para o conector capturar a sessão e começar a consultar.</span>}
            </div>
            <Button size="sm" onClick={abrirPortal} className="bg-red-600 hover:bg-red-700 text-white" data-testid="conector-abrir-portal-button">
              <ExternalLink className="w-4 h-4 mr-2" /> Abrir Lei Não Perturbe no portal
            </Button>
          </div>
        )}
        {conector?.pareado && (
          <div className="rounded-lg border bg-gray-50 dark:bg-gray-800/50 p-3 text-sm grid grid-cols-2 gap-x-4 gap-y-1.5" data-testid="conector-info">
            <span className="text-gray-500">Último contato</span><span data-testid="conector-ultimo-contato">{fmtDataHora(conector.ultimo_contato_em)}</span>
            <span className="text-gray-500">Usuário do portal</span><span className="truncate">{conector.usuario_token || '-'}</span>
            <span className="text-gray-500">Sessão capturada</span><span>{fmtDataHora(conector.token_atualizado_em)}</span>
            <span className="text-gray-500">Consultas pelo conector</span><span>{conector.consultas || 0}{conector.ultima_consulta_em ? ` • última ${fmtDataHora(conector.ultima_consulta_em)}` : ''}</span>
            <span className="text-gray-500">Pareado em</span><span>{fmtDataHora(conector.pareado_em)}{conector.versao ? ` • v${conector.versao}` : ''}</span>
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-dashed p-3">
            <p className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> 1. Instalar (uma vez)</p>
            <ol className="space-y-1.5 text-xs">
              <Passo n="1">Baixe e <b>extraia o .zip</b> em uma pasta fixa (ex.: Documentos\conector-crm).</Passo>
              <Passo n="2">Abra <b>chrome://extensions</b> (Edge: edge://extensions) e ative <b>Modo do desenvolvedor</b>.</Passo>
              <Passo n="3"><b>Carregar sem compactação</b> → escolha a pasta extraída → fixe o ícone roxo na barra.</Passo>
            </ol>
            <Button variant="outline" size="sm" onClick={baixar} disabled={baixando} className="mt-3 w-full" data-testid="conector-download-button">
              {baixando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Baixar conector (.zip)
            </Button>
          </div>
          <div className="rounded-lg border border-dashed p-3">
            <p className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> 2. Parear com o CRM</p>
            <ol className="space-y-1.5 text-xs">
              <Passo n="1">Clique em <b>Gerar link de pareamento</b> e copie.</Passo>
              <Passo n="2">Clique no ícone do conector no navegador → cole o link → <b>Conectar ao CRM</b>.</Passo>
              <Passo n="3">No conector, <b>Abrir Lei Não Perturbe no portal</b> (ou o botão vermelho acima) e faça o login normal. Pronto — fica consultando sozinho.</Passo>
            </ol>
            <Button size="sm" onClick={gerar} disabled={gerando} className="mt-3 w-full bg-purple-600 hover:bg-purple-700" data-testid="conector-parear-button">
              {gerando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />} {conector?.pareado ? 'Gerar novo link (trocar navegador)' : 'Gerar link de pareamento'}
            </Button>
            {link && (
              <div className="mt-2 flex items-center gap-1.5" data-testid="conector-link-box">
                <input readOnly value={link} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 rounded-md border bg-white dark:bg-gray-900 px-2 py-1.5 font-mono text-[11px]" data-testid="conector-link" />
                <Button size="sm" variant="outline" onClick={copiar} data-testid="conector-copiar-button">{copiado ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</Button>
              </div>
            )}
          </div>
        </div>

        <p className="text-[11px] text-gray-500">Dia a dia: deixe a aba do portal aberta (pode minimizar). Se a sessão cair, o ícone mostra “!” e o CRM avisa — é só clicar em <b>Abrir Lei Não Perturbe no portal</b> e logar de novo; nada para copiar.</p>

        {conector?.pareado && (
          <Button variant="ghost" size="sm" onClick={remover} className="text-red-600 hover:text-red-700" data-testid="conector-remover-button"><Unplug className="w-4 h-4 mr-2" /> Desconectar conector</Button>
        )}
      </CardContent>
    </Card>
  );
};
